import {ActionIcon, Button, createStyles} from '@mantine/core';
import React from 'react';

import {trackEvent} from 'analytics/Analytics';
import {assert} from 'Asserts';
import {LLM_CHAT_ANALYTICS_ACTIONS} from 'constants/analytics-products/LLMChatConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ActionOrigin, AgentType} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {openDraftChatInSidepanel} from 'llm/chat-library/sidepanel/ChatRouterUtil';
import {isAgentTypeEnabled} from 'llm/chat-library/sidepanel/ChatTypeUtil';
import {CollectionPageTemplateCollectionNode} from 'notebook/entry-templates/collections/TemplateCollectionTable';
import {
  TemplateCollectionQuery,
  TemplateCollectionQueryVariables,
} from 'notebook/template-collections/graphql/__generated/TemplateCollectionQuery';
import {TEMPLATE_COLLECTION_QUERY} from 'notebook/template-collections/graphql/TemplateCollectionsQueries';
import TemplateCollectionUtil from 'notebook/template-collections/TemplateCollectionUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import MantineDropdownMenu, {
  DropdownMenuWidth,
  MantineDropdownMenuOption,
  MantineDropdownMenuSection,
} from 'ui-library/menus/MantineDropdownMenu';

import NewTemplateCollectionButton from './NewTemplateCollectionButton';

const TEMPLATE_FROM_COMPOSE_LABEL = 'Entry template using AI';
const TEMPLATE_FROM_COMPOSE_PROMPT =
  'Help me start a draft that I can then save as a template. Ask me one or two questions to get started.';

export interface TemplateCollectibleCreateButtonInternalProps {
  collection: CollectionPageTemplateCollectionNode;
  text?: string;
  disabled?: boolean;
  // TODO(BNCH-121653): We should be able to make compact required and remove text here
  compact?: boolean;
  // TODO(BNCH-121653) - Currently, the TemplateCollectibleCreateButton is used in both the
  // `TemplateCollectionsSidepanel` and in the `TemplateCollectionTable`. The way that we render it is
  // slightly different but the functionality is identical in both. The `legacyMode` flag here is intended to
  // provide signal for when we are in the `TemplateCollectionTable`. Once we remove
  // `ENABLE_TEMPLATE_COLLECTIONS_IN_WORKSPACE`, we can remove the `legacyMode` flag.
  legacyMode?: boolean;
}

function TemplateCollectibleCreateButtonInternal({
  collection,
  disabled,
  compact,
  legacyMode = true,
  text = 'Create',
}: TemplateCollectibleCreateButtonInternalProps): JSX.Element {
  const canCreateCollectibles = collection.permissions && collection.permissions.appendable;
  const templateTypes = TemplateCollectionUtil.getTemplateCollectionTypes(collection);

  const showAIOption = isAgentTypeEnabled(AgentType.ASSISTANT);

  function buildTemplateTypeOption(value: ModelTypeConstants): MantineDropdownMenuOption | null {
    const templateType = templateTypes.find((t) => t.value === value);
    if (!templateType) {
      return null;
    }
    const menuState = templateType.getCreateMenuState(collection);
    if (menuState.kind === 'hidden') {
      return null;
    }
    return {
      name: templateType.name,
      icon: templateType.icon,
      onSelect: templateType.createTemplateTypeClickHandler,
      isDisabled: menuState.kind === 'disabled',
      disabledTooltip: menuState.kind === 'disabled' ? menuState.reason : undefined,
      dataTestAttrs: {component: 'TemplateCollectionPage', element: 'option', key: templateType.name},
    };
  }

  const aiOption: MantineDropdownMenuOption = {
    name: TEMPLATE_FROM_COMPOSE_LABEL,
    icon: 'sparkle',
    onSelect: () => {
      trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.CLICKED_TEMPLATE_FROM_COMPOSE, {
        actionOrigin: ActionOrigin.NOTEBOOK_TEMPLATE_COLLECTION_PAGE,
      });

      openDraftChatInSidepanel(AgentType.ASSISTANT, TEMPLATE_FROM_COMPOSE_PROMPT);
    },
    dataTestAttrs: {component: 'TemplateCollectionPage', element: 'option', key: TEMPLATE_FROM_COMPOSE_LABEL},
  };

  const sections: Array<MantineDropdownMenuSection> = [
    {
      items: [
        buildTemplateTypeOption(ModelTypeConstants.TEMPLATE),
        showAIOption ? aiOption : null,
        buildTemplateTypeOption(ModelTypeConstants.SUBTEMPLATE),
      ].filter((item): item is MantineDropdownMenuOption => item != null),
    },
    {
      items: [buildTemplateTypeOption(ModelTypeConstants.ANALYSIS_TEMPLATE)].filter(
        (item): item is MantineDropdownMenuOption => item != null
      ),
    },
    {
      items: [
        buildTemplateTypeOption(ModelTypeConstants.FORM_DEFINITION),
        buildTemplateTypeOption(ModelTypeConstants.REQUEST_V2_DEFINITION),
      ].filter((item): item is MantineDropdownMenuOption => item != null),
    },
    {
      items: [
        buildTemplateTypeOption(ModelTypeConstants.ASSAY_PROCEDURE),
        buildTemplateTypeOption(ModelTypeConstants.RECIPE_PROCEDURE),
      ].filter((item): item is MantineDropdownMenuOption => item != null),
    },
    {
      items: [buildTemplateTypeOption(ModelTypeConstants.PLATE_DESIGN_TEMPLATE)].filter(
        (item): item is MantineDropdownMenuOption => item != null
      ),
    },
  ].filter((section) => section.items.length > 0);

  const isDisabled = Boolean(disabled || !canCreateCollectibles);
  const triggerTestProps = getTestProps({
    component: 'TemplateCollectionPage',
    element: 'button',
    key: 'create',
  });

  const trigger = legacyMode ? (
    <Button
      variant="primary"
      size="sm"
      rightIcon={<BenchlingIcon icon="chevron-down" />}
      disabled={isDisabled}
      {...triggerTestProps}
    >
      {text}
    </Button>
  ) : (
    <ActionIcon
      variant={compact ? 'ghost' : 'secondary'}
      size="sm"
      radius="sm"
      disabled={isDisabled}
      ml={6}
      {...triggerTestProps}
    >
      <BenchlingIcon icon="compose" />
    </ActionIcon>
  );

  const {classes} = useStyles();

  return (
    <MantineDropdownMenu
      trigger={trigger}
      sections={sections}
      width={DropdownMenuWidth.LARGE}
      position={legacyMode || !compact ? 'bottom-end' : 'bottom-start'}
      itemClassName={classes.item}
      itemIconClassName={classes.itemIcon}
      onOpenChange={(opened) => {
        if (opened && showAIOption) {
          trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.VIEWED_TEMPLATE_FROM_COMPOSE, {
            actionOrigin: ActionOrigin.NOTEBOOK_TEMPLATE_COLLECTION_PAGE,
          });
        }
      }}
    />
  );
}

const useStyles = createStyles((theme) => ({
  item: {
    gap: theme.spacing.xs,
  },
  itemIcon: {
    color: theme.colors.gray[5],
  },
}));

interface TemplateCollectibleCreateButtonProps extends Omit<
  TemplateCollectibleCreateButtonInternalProps,
  'collection'
> {
  collectionId: string;
}

function TemplateCollectibleCreateButton({
  collectionId,
  compact,
  legacyMode,
  disabled = false,
  text,
}: TemplateCollectibleCreateButtonProps): JSX.Element {
  // TODO(BNCH-123598): Remove query once we can pass down the full Collection object
  return (
    <BenchlingQuery<TemplateCollectionQuery, TemplateCollectionQueryVariables>
      query={TEMPLATE_COLLECTION_QUERY}
      variables={{id: collectionId}}
      renderLoading={() => <CenteredHorizontalThrobber />}
    >
      {(data) => {
        assert(!isInaccessible(data.templateCollection), 'Expected result schema to be accessible.');
        const collection = data.templateCollection;
        return (
          <TemplateCollectibleCreateButtonInternal
            compact={compact}
            collection={collection}
            disabled={disabled}
            legacyMode={legacyMode}
            text={text}
          />
        );
      }}
    </BenchlingQuery>
  );
}

interface TemplateCollectionOrCollectibleCreateButtonProps extends Omit<
  TemplateCollectibleCreateButtonProps,
  'collectionId'
> {
  collectionId?: string;
}

export default function TemplateCollectionOrCollectibleCreateButton(
  props: TemplateCollectionOrCollectibleCreateButtonProps
): JSX.Element {
  const {collectionId} = props;
  if (collectionId !== undefined) {
    return <TemplateCollectibleCreateButton {...props} collectionId={collectionId} />;
  }
  return <NewTemplateCollectionButton {...props} compact={props.compact ?? true} />;
}
